import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DialogActions, Grid, IconButton, Link, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import RequestListSkeletonCard from '../eph-dcr-tracker/skeleton/requestListCard';
import { DirectionContext } from '../../context/DirectionProvider';
import { ContentCopy, Done } from '@mui/icons-material';
import api from '../../service/api';

export default function SearchBeforeSubmit(props) {
    const direction = React.useContext(DirectionContext);

    const [open, setOpen] = React.useState(false);
    const [fullWidth, setFullWidth] = React.useState(true);
    const [skeletonView, setSkeletonView] = React.useState(true)
    const [progressBar, setProgressBar] = React.useState(false)
    const [rowsPerPage, setRowsPerPage] = React.useState(5)
    const [rowsArray, setRowsArray] = React.useState([])
    const [page, setPage] = React.useState(0)
    const [nextToken, setNextToken] = React.useState()
    const [totalCount, setTotalCount] = React.useState(null)

    const [allItems, setAllItems] = React.useState()
    const [isCopied, setIsCopied] = React.useState(null)

    const isMounted = React.useRef(true)

    React.useEffect(() => {
        isMounted.current = true
        return () => { isMounted.current = false }
    }, [])

    React.useEffect(() => {
        if(props?.open) {
            const raw = props?.sbsData?.sbc_results
            const results = Array.isArray(raw) ? raw : (raw ? [raw] : [])
            setNextToken(props?.sbsData?.nextToken)
            setAllItems(prev => [...(prev || []), ...results])
            setTotalCount(props?.sbsData?.total_count ?? totalCount)
            setSkeletonView(false)
        }        
    }, [])
    
    const handleApiCall = async (token = nextToken) => {
        setProgressBar(true)
        const payload = {
            updatedValue: props?.sbsPayload,
            is_sbc_request: true
        }
        // const payload = {"is_sbc_request": true, "updatedValue": {"entity": {"type": "configuration/entityTypes/HCO","ActiveFlag": [{"value": "Y"}],"EffectiveStartDate": [{"value": "2026-05-26"}],"EffectiveEndDate": [{"value": "9999-12-31"}],"NewSpecialities": {},"NewIdentifiers": [],"Address": [{"Country": [{"value": "United States"}],"ActiveFlag": [{"value": "Y"}],"AddressLine1": [{"value": "St 1"}],"City": [{"value": "New York"}],"Zip": [{"value": "123456"}],"StateProvince": [{"value": "NY"}],"Rank": [{"value": "1"}],"EffectiveStartDate": [{"value": "2026-05-26"}],"EffectiveEndDate": [{"value": "9999-12-31"}]}],"Source": [{"value": "Employer"}],"CountryCode": [{"value": "United States"}],"Name": [{"value": "Test Emp HCO"}],"ClassificationCode": [{"value": "ADMINISTRATOR | C001 | C331"}]},"relations": [{"uri": "7mLpmdv","type": "configuration/entityTypes/HCO","label": "BEAZER HOMES USA INC","EffectiveStartDate": [{"value": "2026-05-26"}],"EffectiveEndDate": [{"value": "9999-12-31"}],"Rank": [{"value": "1"}],"Source": [{"value": "JudyDiamond"}],"DepartmentIndicator": [{"value": "N"}],"IsNew": "true","IsPrimary": "true","IsParent": "true","relationTypeCode": "A workplace refers to Clinic | AFRTP071 | AFRTP071"}],"comments": "Testing Employer Payload"}}
        try {
            const res = await api.postUpdate(payload, null, token)
            if (res && res.data) {
                const raw = res.data?.sbc_results
                const results = Array.isArray(raw) ? raw : (raw ? [raw] : [])
                const added = results.length
                if (isMounted.current) {
                    setNextToken(res?.data?.nextToken)
                    setAllItems(prev => [...(prev || []), ...results])
                    setTotalCount(res?.data?.total_count ?? totalCount)
                }
                return { added, nextToken: res?.data?.nextToken }
            }
        } catch (err) {
            // ignore errors for now
        } finally {
            if (isMounted.current) {
                setProgressBar(false)
                setSkeletonView(false)
            }
        }
        return { added: 0, nextToken: null }
    }

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getTableRows = () => {
        const columns = [
            { id: 'label', label: 'Name', minWidth: 170 },
            { id: 'EnterpriseId', label: 'Lilly Enterprise ID', minWidth: 170 },
            { id: 'ReferenceId', label: 'Reference ID', minWidth: 170 },
            { id: 'ClassificationCode', label: 'Classification Code', minWidth: 100 },
            { id: 'Action', label: 'Action', minWidth: 100 },
        ];
        return columns;
    }

    const getRowArray = (allItems) => {
        let counter = 10;
        rowsArray.splice(0, rowsArray.length);
        (allItems.length / 2 < counter) && rowsArray.push(allItems.length)
        while ((allItems.length / 2) > counter) {
            rowsArray.push(counter)
            counter += 10;
        }
        return rowsArray;
    }

    const handleCopyClipboard = (event, data) => {
        event.stopPropagation();
        navigator.clipboard.writeText(data);
        setIsCopied(data);
        setTimeout(() => setIsCopied(null), 1000);
    };

    const handleChangePage = async (event, newPage) => {
        const needUntilIndex = (newPage + 1) * rowsPerPage;
        let localLoaded = allItems.length
        let token = nextToken
        while (localLoaded < needUntilIndex && token) {
            // eslint-disable-next-line no-await-in-loop
            const { added, nextToken: nt } = await handleApiCall(token)
            localLoaded += added
            token = nt
            if (!token) break
        }
        if (isMounted.current) setPage(newPage);
    };

    const handleChangeRowsPerPage = async (event) => {
        const newRpp = parseInt(event.target.value, 10);
        setRowsPerPage(newRpp);
        setPage(0);
        const needUntilIndex = newRpp; // first page
        let localLoaded = allItems.length
        let token = nextToken
        while (localLoaded < needUntilIndex && token) {
            // eslint-disable-next-line no-await-in-loop
            const { added, nextToken: nt } = await handleApiCall(token)
            localLoaded += added
            token = nt
            if (!token) break
        }
    };

    const visibleRows = allItems ? allItems?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : [];

    return (
        <React.Fragment>
            {/* <Button variant="outlined" onClick={handleClickOpen}>
                Open SBS
            </Button> */}
            <Dialog
                fullWidth={fullWidth}
                maxWidth={'lg'}
                open={props?.open}
                onClose={props?.handleClose}
            >
                <DialogTitle id='dialog-title'>
                    <Typography sx={{ color: "#DC3545", fontSize: "20px" }}>Similar Profiles</Typography>
                    <Typography sx={{ color: "#707070", fontSize: "14px" }}>We have found similar profiles based on your inputs.</Typography>
                </DialogTitle>
                <DialogContent dividers id='newhcp-text'>
                        {/* skeleton placeholders moved inside table body */}
                    <TableContainer className='fw-bold' sx={{ maxHeight: 540, overflowY: 'auto' }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead id="table-heading">
                                <TableRow>
                                    {getTableRows().map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            sx={{ minWidth: column.minWidth, backgroundColor: '#eee', top: 0, zIndex: 2 }}
                                        >
                                            <span>{column?.label}</span>
                                        </TableCell>

                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody id="search-table-data">
                                {
                                    (progressBar || skeletonView) ? (
                                        Array(rowsPerPage).fill(null).map((_, rIdx) => (
                                            <TableRow hover role="checkbox" key={`skeleton-${rIdx}`}>
                                                {getTableRows().map((column) => (
                                                    <TableCell key={column.id} align={column.align}>
                                                        <div style={{ backgroundColor: '#eef2f7', height: 18, borderRadius: 4, width: column.id === 'label' ? '60%' : '100%' }} />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : (
                                        visibleRows && visibleRows.map((row, i) => {
                                            return (
                                                <TableRow hover role="checkbox" key={row._id || `${row.EnterpriseId}-${i}`}>
                                                    {
                                                        getTableRows().map((column) => {
                                                            let tableValue = row?.[column?.id];
                                                            if (column.id === 'EnterpriseId') {
                                                                if(props?.entity === 'HCO') {
                                                                    tableValue = row?.attributes?.['HCOUniqueId'];
                                                                } else {
                                                                    tableValue = row?.attributes?.['HCPUniqueId'];
                                                                }
                                                            }
                                                            if (column.id === 'ReferenceId') {
                                                                if(props?.entity === 'HCO') {
                                                                    tableValue = row?.attributes?.['HCOReferenceId'];
                                                                } else {
                                                                    tableValue = row?.attributes?.['HCPReferenceId'];
                                                                }
                                                            }
                                                            if (column.id === 'ClassificationCode') {
                                                                tableValue = row?.attributes?.['ClassificationCode'];
                                                            }
                                                            if (tableValue instanceof Array && tableValue && tableValue.length > 0) {
                                                                tableValue = tableValue[0].value;
                                                            }

                                                            return (
                                                                <React.Fragment>
                                                                    <TableCell key={column.id} align={column.align}>
                                                                        {
                                                                            column?.id === 'Action' &&
                                                                            <>
                                                                                <Button
                                                                                    onClick={() => {
                                                                                        // Open the /about route in a new tab
                                                                                        window.open(`/details/${row?.uri?.split('/')?.[row.uri.split('/').length -1 ]}/${row?.type?.split('/')?.[row.type.split('/').length -1 ]}`, "_blank", "noopener,noreferrer");
                                                                                    }}
                                                                                    color="primary"
                                                                                    variant="outlined"
                                                                                    autoFocus
                                                                                    size='small'
                                                                                    sx={{ marginRight: 2, textTransform: 'capitalize' }}
                                                                                >
                                                                                    View Profile
                                                                                </Button>
                                                                                {/* <Button
                                                                                    color="success"
                                                                                    variant="outlined"
                                                                                    autoFocus
                                                                                    size='small'
                                                                                    sx={{ textTransform: 'capitalize' }}
                                                                                    onClick={() => window.location.href = (`/details/${row?.uri?.split('/')?.[row.uri.split('/').length -1 ]}/${row?.type?.split('/')?.[row.type.split('/').length -1 ]}`)}
                                                                                >
                                                                                    Continue with this Profile
                                                                                </Button> */}
                                                                            </>
                                                                        }
                                                                        {
                                                                            column.id === 'EnterpriseId' ?
                                                                                <>
                                                                                    {tableValue}
                                                                                    <Tooltip title={isCopied && isCopied === row.Id ? "Copied!" : "Click to Copy"}>
                                                                                        <IconButton size="small" aria-label="copy" color="primary" onClick={(e) => handleCopyClipboard(e, tableValue)}>
                                                                                            {isCopied && isCopied === row.EnterpriseId ? <Done fontSize="10px" /> : <ContentCopy fontSize={'10px'} />}
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </>
                                                                                :
                                                                                tableValue
                                                                        }
                                                                    </TableCell>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                </TableRow>
                                            );
                                        })
                                    )
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        id="pagination"
                        count={totalCount ?? allItems?.length ?? 0}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelDisplayedRows={({ from, to, count }) =>
                            `Showing ${from}-${to} of ${count}`
                        }
                        labelRowsPerPage="Rows per page:"
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            "& .MuiTablePagination-toolbar": {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                px: 2,
                                py: 1,
                                width: "100%",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                margin: 0,
                                marginRight: "auto",
                                fontSize: "0.875rem",
                                color: "#374151",
                            },
                            "& .MuiTablePagination-selectLabel": {
                                margin: 0,
                                fontSize: "0.875rem",
                                color: "#374151",
                            },
                            "& .MuiTablePagination-select": {
                                fontSize: "0.875rem",
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions className='_wg-dynamic-text'>
                    <Button
                        onClick={() => props?.handleClose()}
                        color='error'
                        variant='outlined'
                    >
                        Cancel
                    </Button>
                    <Button onClick={() => props?.handleSkip()} color='error' variant='contained'>
                        Skip and Create New
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
